import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface AdminSubscription {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  plan_tier: string;
  plan_name: string;
  status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_start: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
}

interface ListSubscriptionsParams {
  status?: string;
  page?: number;
  limit?: number;
}

async function invokeAdminApi<T>(action: string, params?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-api", {
    body: { action, ...params },
  });

  if (error) throw new Error(error.message || "Erro na API admin");
  if (!data.success) throw new Error(data.error || "Erro desconhecido");

  return data.data as T;
}

export function useAdminSubscriptions(params: ListSubscriptionsParams = {}) {
  const { status, page = 1, limit = 20 } = params;

  return useQuery({
    queryKey: ["admin-subscriptions", status, page, limit],
    queryFn: () =>
      invokeAdminApi<{ subscriptions: AdminSubscription[]; total: number }>(
        "list_subscriptions",
        { status, page, limit }
      ),
    staleTime: 30 * 1000,
  });
}

export function useOverridePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { subscription_id: string; plan_id: string }) =>
      invokeAdminApi("override_plan", params),
    onSuccess: () => {
      toast({ title: "Plano alterado com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao alterar plano", variant: "destructive" });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { subscription_id: string }) =>
      invokeAdminApi("cancel_subscription", params),
    onSuccess: () => {
      toast({ title: "Assinatura cancelada" });
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao cancelar assinatura", variant: "destructive" });
    },
  });
}

export function useExtendTrial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { subscription_id: string; new_trial_end: string }) =>
      invokeAdminApi("extend_trial", params),
    onSuccess: () => {
      toast({ title: "Trial estendido com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao estender trial", variant: "destructive" });
    },
  });
}
